import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import {
    AiEditAttachmentDto,
    AiEditChange,
    AiEditChangeEvent,
    AiEditChangeStatus,
    AiEditConflictEvent,
    AiEditConflictInfo,
    AiEditConflictResolution,
    AiEditHistoryEntryDto,
    AiEditRunStatusDto,
    AiEditStatusEvent,
    AiEditTurn,
    DocumentAiEditController,
    DocumentItemTypeEnum,
    IaChatController,
    UserController,
} from '@domain';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, finalize, from, Observable, of, switchMap, take } from 'rxjs';
import { MediaUtils } from 'src/app/shared/utils/media.utils';
import {
    EditDataService,
    PREVIEW_TOP_ITEM,
    PreviewHighlight,
    PreviewHighlightMark,
} from './_edit-data.service';

/**
 * Etapa do pedido como a TELA a vê (docs/EDITOR-CHAT-IA.md, seção 3):
 * 1 `sending` → 2 `thinking` → 3 `applying` → 4 aplicado (vira mensagem; a tela volta a
 * `idle` com a ilha de Desfazer). `error` é o pedido inteiro que falhou.
 */
export type AiEditUiStage = 'idle' | 'sending' | 'thinking' | 'applying' | 'error';

/** Foto ou vídeo mostrado na bolha do usuário. */
export interface AiEditMessageMedia {
    url: string;
    kind: 'image' | 'video';
    thumbUrl?: string;
}

/** Anexo escolhido (ou arrastado) e ainda não enviado: sobe ao storage na hora. */
export interface AiEditPendingAttachment {
    id: string;
    kind: 'image' | 'video';
    name: string;
    /** `blob:` local para a miniatura aparecer antes de o upload terminar. */
    previewUrl: string;
    status: 'uploading' | 'done' | 'error';
    progress: number;
    /** Preenchidos quando o upload termina. */
    url?: string;
    size: number;
    thumbUrl?: string;
    duration?: number;
}

/** Uma fala da conversa do modo IA. */
export interface AiEditMessage {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    createdAt: string;
    /** Fala do usuário ainda sem o 202 da API (bolha otimista, "Enviando"). */
    sending?: boolean;
    /** Resposta de um pedido concluído: o run que a gerou. */
    taskId?: string;
    /** As alterações daquele pedido, congeladas no fim. */
    changes?: AiEditChange[];
    /** Próximos passos sugeridos pela IA (chips). */
    suggestions?: string[];
    /** O usuário desfez as alterações deste pedido. */
    reverted?: boolean;
    /** Pedido que falhou por inteiro; `text` é a chave i18n do motivo. */
    error?: boolean;
    /** Pedido do usuário que esta falha deixou sem resposta ("Tentar de novo"). */
    retryText?: string;
    /** Anexos daquele pedido, para o "Tentar de novo" mandar junto. */
    retryAttachments?: AiEditAttachmentDto[];
    /** Fotos e vídeos que o usuário anexou nesta fala. */
    media?: AiEditMessageMedia[];
    /** O usuário parou este pedido (o que já tinha entrado fica). */
    cancelled?: boolean;
    /** A IA pesquisou na internet para responder este pedido. */
    webSearched?: boolean;
}

/** O pedido em andamento. `taskId` é null até o 202 chegar. */
interface AiEditRun {
    taskId: string | null;
    userMessageId: string;
    text: string;
    attachments: AiEditAttachmentDto[];
}

/** Ilha "N alterações aplicadas · Desfazer" sobre a prévia. */
export interface AiEditUndoOffer {
    taskId: string;
    count: number;
}

/**
 * Pendências do último pedido: conflitos esperando a decisão do usuário e alterações que
 * falharam (estados 5 e 6 do doc). A ilha sobre a prévia e o realce laranja/vermelho vêm daqui.
 */
export interface AiEditAttention {
    taskId: string;
    applied: number;
    total: number;
    conflicts: number;
    errors: number;
}

/** Situações em que uma alteração espera o usuário (decidir / tentar de novo). */
const ATTENTION_STATUSES = new Set<AiEditChangeStatus>(['conflict', 'error']);

/** As alterações de um pedido que esperam o usuário, na ordem do plano. */
export function attentionChanges(changes: AiEditChange[] | undefined): AiEditChange[] {
    return (changes ?? []).filter(change => ATTENTION_STATUSES.has(change.status));
}

/** Realces que ficam na prévia depois do pedido: conflitos em laranja, falhas em vermelho. */
export function pendingMarks(changes: AiEditChange[] | undefined): PreviewHighlightMark[] {
    const ids = (status: AiEditChangeStatus) =>
        (changes ?? []).filter(change => change.status === status).flatMap(change => change.fieldIds);
    return [
        { itemIds: ids('conflict'), tone: 'conflict' as const },
        { itemIds: ids('error'), tone: 'error' as const },
    ].filter(mark => mark.itemIds.length);
}

/** Sem nenhum evento do pedido por este tempo, a tela desiste (seção 8: timeout do job). */
/**
 * Silêncio que faz o chat CONSULTAR a situação do pedido. Não é prazo: o pedido pode levar
 * o tempo que for; só termina sem resposta se o servidor disser que ele morreu (a API
 * reiniciou no meio) ou se o usuário parar.
 */
export const AI_EDIT_SILENCE_CHECK_MS = 60_000;
/** A ilha verde com Desfazer fica este tempo na tela (pausa com o mouse em cima). */
export const AI_EDIT_UNDO_WINDOW_MS = 8_000;
/** Duração do flash azul nos blocos alterados, quando o pedido termina. */
export const AI_EDIT_DONE_FLASH_MS = 1_600;
/** Anexos por pedido (mesmo teto da API). */
export const AI_EDIT_MAX_ATTACHMENTS = 20;

/** Alterações que acontecem na primeira folha (sem item para rolar até ele). */
const COVER_KINDS = new Set<AiEditChange['kind']>([
    'update_cover',
    'change_cover_template',
    'rename_document',
]);

/** Primeira mensagem de erro da API (`{ message: string[] }`), quando houver. */
/** Status HTTP de um erro do HttpClient (0 quando não há). */
function httpStatus(error: unknown): number {
    const status = (error as { status?: unknown })?.status;
    return typeof status === 'number' ? status : 0;
}

/** Etapas em que o pedido ainda está vivo no servidor. */
const ACTIVE_STAGES = new Set(['queued', 'planning', 'applying']);

function apiErrorMessage(error: unknown): string | null {
    const message = (error as { error?: { message?: unknown } })?.error?.message;
    const first = Array.isArray(message) ? message[0] : message;
    return typeof first === 'string' && first.trim() ? first : null;
}

const newId = (): string =>
    `${Date.now()}-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;

/**
 * Estado do modo "Editar com IA": a conversa, o pedido em andamento e o que a tela mostra
 * em cada etapa. Recebe os eventos `ai-edit-status` / `ai-edit-conflict` do `EditService`.
 *
 * As ALTERAÇÕES em si não passam por aqui: elas chegam nos eventos de campo que a edição
 * humana já emite e caem direto no `EditDataService` (a prévia repagina sozinha). Aqui só
 * se acompanha o andamento, se realça o que mudou e se oferece o Desfazer.
 */
@Injectable({ providedIn: 'root' })
export class AiEditStore {
    // * [Injects]
    private _controller = inject(DocumentAiEditController);
    private _media = inject(IaChatController);
    private _mediaUtils = inject(MediaUtils);
    private _editData = inject(EditDataService);
    private _userController = inject(UserController);
    private _translate = inject(TranslateService);

    /** Conversa anterior sendo carregada ao abrir o documento. */
    public readonly restoring = signal(false);
    /** Documento cuja conversa já foi pedida à API (uma vez por visita). */
    private _restoredFor: string | null = null;

    constructor() {
        // Ao abrir um documento, o chat reabre com a conversa que a API guardou: o
        // histórico sobrevive a recarregar a tela e a entrar de novo no documento.
        effect(() => {
            const documentId = this._editData.document()?._id;
            untracked(() => this.restoreHistory(documentId));
        });
    }

    /**
     * Carrega a conversa anterior deste documento (uma vez por visita). Não mexe num chat
     * que já tem falas, nem num pedido em andamento.
     */
    public restoreHistory(documentId: string | undefined): void {
        if (!documentId || documentId === this._restoredFor) return;
        this._restoredFor = documentId;
        if (this.messages().length || this.currentRun()) return;

        this.restoring.set(true);
        this._controller
            .history(documentId)
            .pipe(take(1))
            .subscribe({
                next: entries => {
                    // A tela pode ter trocado de documento (ou o usuário já ter falado)
                    // enquanto a resposta vinha: aí o histórico não vale mais.
                    if (this._editData.document()?._id !== documentId) return;
                    if (this.messages().length || this.currentRun()) return;
                    this.messages.set(this._fromHistory(entries ?? []));
                    // Pedido ainda rodando (a tela foi recarregada no meio): o chat volta
                    // acompanhando ele, com o "pensando" na tela.
                    const active = this._activeEntry(entries ?? []);
                    if (active) this._attach(active);
                    // Conflito ou falha que ficou do último pedido: a prévia volta a marcar.
                    else this._showPending();
                },
                complete: () => this.restoring.set(false),
                error: () => this.restoring.set(false),
            });
    }

    /** Os pedidos gravados viram as mesmas bolhas de uma conversa ao vivo. */
    private _fromHistory(entries: AiEditHistoryEntryDto[]): AiEditMessage[] {
        const messages: AiEditMessage[] = [];
        const lastDone = [...entries].reverse().find(entry => entry.stage === 'done');

        for (const entry of entries) {
            if (entry.stage !== 'done' && entry.stage !== 'error' && entry.stage !== 'cancelled') continue;

            messages.push({
                id: newId(),
                role: 'user',
                text: entry.message,
                createdAt: entry.createdAt,
                media: (entry.attachments ?? []).map(a => ({
                    url: a.url,
                    kind: a.type,
                    thumbUrl: a.thumbUrl,
                })),
            });

            if (entry.stage === 'cancelled') {
                messages.push({
                    id: newId(),
                    role: 'assistant',
                    text: entry.reply?.trim() || this._translate.instant('document.edit.aiEdit.cancelledDefault'),
                    createdAt: entry.createdAt,
                    taskId: entry.taskId,
                    changes: (entry.changes ?? []).filter(change => change.status === 'applied'),
                    suggestions: [],
                    reverted: !!entry.revertedAt,
                    cancelled: true,
                });
                continue;
            }

            if (entry.stage === 'error') {
                messages.push({
                    id: newId(),
                    role: 'assistant',
                    text: entry.error || 'document.edit.aiEdit.errorDefault',
                    createdAt: entry.createdAt,
                    error: true,
                    retryText: entry.message,
                    retryAttachments: entry.attachments?.length
                        ? entry.attachments.map(a => ({ url: a.url, type: a.type, thumbUrl: a.thumbUrl }))
                        : undefined,
                });
                continue;
            }

            messages.push({
                id: newId(),
                role: 'assistant',
                text: entry.reply?.trim() || this._translate.instant('document.edit.aiEdit.doneDefault'),
                createdAt: entry.createdAt,
                taskId: entry.taskId,
                changes: entry.changes ?? [],
                // Só os próximos passos do último pedido fazem sentido como chips.
                suggestions: entry === lastDone ? (entry.suggestions ?? []) : [],
                reverted: !!entry.revertedAt,
                webSearched: !!entry.webSearched,
            });
        }

        return messages;
    }

    // * [State]
    public messages = signal<AiEditMessage[]>([]);
    public draft = signal('');
    /** Anexos do próximo pedido (botão de anexar, arrastar ou colar). */
    public attachments = signal<AiEditPendingAttachment[]>([]);
    public uploading = computed(() => this.attachments().some(a => a.status === 'uploading'));
    /** Dá para enviar: texto ou anexo pronto, sem upload em andamento. */
    public canSend = computed(
        () =>
            !this.busy() &&
            !this.uploading() &&
            (!!this.draft().trim() || this.attachments().some(a => a.status === 'done'))
    );
    public stage = signal<AiEditUiStage>('idle');
    /** Texto da etapa vindo do backend (ex.: "Lendo as 5 seções…"); null = o padrão. */
    public stageText = signal<string | null>(null);
    /** Plano do pedido em andamento, com a situação de cada alteração. */
    public changes = signal<AiEditChange[]>([]);
    public currentRun = signal<AiEditRun | null>(null);
    public undoOffer = signal<AiEditUndoOffer | null>(null);
    public reverting = signal(false);
    /** Pedido de parada enviado, esperando o servidor confirmar. */
    public cancelling = signal(false);
    /** Alteração sendo decidida ou retentada agora (id): o cartão dela trava até a resposta. */
    public settling = signal<string | null>(null);

    /**
     * Pendências do último pedido que mexeu no documento (conflitos e falhas). Nulo quando
     * não há nada esperando o usuário, ou quando o pedido foi desfeito.
     */
    public attention = computed<AiEditAttention | null>(() => {
        const last = [...this.messages()].reverse().find(message => message.taskId && message.changes?.length);
        if (!last || last.reverted) return null;
        const changes = last.changes!;
        const conflicts = changes.filter(change => change.status === 'conflict').length;
        const errors = changes.filter(change => change.status === 'error').length;
        if (!conflicts && !errors) return null;
        return {
            taskId: last.taskId!,
            applied: changes.filter(change => change.status === 'applied').length,
            total: changes.length,
            conflicts,
            errors,
        };
    });

    /** Quando o pedido em andamento começou (relógio da tela) e o "agora" que anda de 1 em 1 s. */
    private _runStartedAt = signal<number | null>(null);
    private _now = signal(Date.now());
    private _clock?: ReturnType<typeof setInterval>;

    /** Tempo de trabalho do pedido em andamento ("12s", "1m 05s", "1h 02m"). */
    public elapsedText = computed(() => {
        const started = this._runStartedAt();
        if (started == null) return '';
        const total = Math.max(0, Math.floor((this._now() - started) / 1000));
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        const two = (n: number) => String(n).padStart(2, '0');
        if (hours) return `${hours}h ${two(minutes)}m`;
        if (minutes) return `${minutes}m ${two(seconds)}s`;
        return `${seconds}s`;
    });

    /** Pedido em andamento: o composer trava até ele terminar. */
    public busy = computed(() => {
        const stage = this.stage();
        return stage === 'sending' || stage === 'thinking' || stage === 'applying';
    });

    public appliedCount = computed(
        () => this.changes().filter(change => change.status === 'applied').length
    );

    /** O pedido mais recente que ainda pode ser desfeito (chip "Desfazer a última alteração"). */
    public lastUndoableTaskId = computed(() => {
        const last = [...this.messages()].reverse().find(message => message.taskId);
        return last && !last.reverted && last.changes?.some(c => c.status === 'applied')
            ? last.taskId!
            : null;
    });

    // * [Internos]
    /** Eventos do pedido que chegaram antes do 202 (o job pode ser mais rápido que a resposta). */
    private _early: AiEditStatusEvent[] = [];
    private _timeout?: ReturnType<typeof setTimeout>;
    private _flashTimer?: ReturnType<typeof setTimeout>;
    private _undoTimer?: ReturnType<typeof setTimeout>;
    private _undoDeadline = 0;
    private _undoRemaining = 0;
    /**
     * Pedidos que o chat deu como perdidos (sem sinal da API por um tempo) mas que podem
     * estar vivos no servidor: se um evento deles chegar, o chat retoma o acompanhamento e
     * tira o aviso de erro.
     */
    private _abandoned = new Map<string, AiEditRun & { errorMessageId: string }>();

    // ─────────────────────────────────────────────────────────── pedido

    /**
     * Envia um pedido de alteração, com os anexos prontos. Ignorado enquanto outro está em
     * andamento ou enquanto um anexo ainda sobe. Só anexo, sem texto, também vale: a IA
     * coloca a mídia onde fizer mais sentido.
     */
    public send(text: string, retryAttachments?: AiEditAttachmentDto[]): void {
        const message = text.trim();
        const documentId = this._editData.document()?._id;
        const attachments = retryAttachments ?? this._readyAttachments();
        if ((!message && !attachments.length) || !documentId || this.busy() || this.uploading())
            return;

        const turns = this._turns();
        const userMessage: AiEditMessage = {
            id: newId(),
            role: 'user',
            text: message,
            createdAt: new Date().toISOString(),
            sending: true,
            media: attachments.map(a => ({
                url: a.url,
                kind: a.type,
                thumbUrl: a.thumbUrl,
            })),
        };

        this.messages.update(messages => [...messages, userMessage]);
        this.draft.set('');
        // Os arquivos já estão no storage e vão no pedido: só a fila local é limpa.
        if (!retryAttachments) this._clearAttachments(false);
        this.stage.set('sending');
        this.stageText.set(null);
        this.changes.set([]);
        this._dismissUndo();
        this._early = [];
        this.currentRun.set({
            taskId: null,
            userMessageId: userMessage.id,
            text: message,
            attachments,
        });
        this._startClock(Date.now());
        this._armTimeout();

        this._controller
            .start({
                documentId,
                sessionId: this._editData.currentSession()?._id,
                message,
                turns,
                locale: this._translate.currentLang ?? this._translate.defaultLang ?? 'pt-BR',
                attachments,
            })
            .pipe(take(1))
            .subscribe({
                next: ({ taskId }) => this._accepted(userMessage.id, taskId),
                // A API explica o motivo quando sabe (outro pedido em andamento, sem saldo):
                // vale mais que o genérico "não foi possível enviar".
                error: error =>
                    httpStatus(error) === 409
                        ? this._followActive(userMessage.id, documentId, error)
                        : this._fail(apiErrorMessage(error) ?? 'document.edit.aiEdit.errorSend'),
            });
    }

    /**
     * A API recusou porque já há um pedido rodando neste documento: em vez de mostrar um
     * erro (e deixar a tela parada, sem sinal de que a IA trabalha), o chat passa a
     * acompanhar aquele pedido. O texto recusado volta para o campo, para mandar depois.
     */
    private _followActive(userMessageId: string, documentId: string, error: unknown): void {
        const refused = this.currentRun();
        this._controller
            .history(documentId)
            .pipe(take(1))
            .subscribe(entries => {
                const active = this._activeEntry(entries ?? []);
                if (!active) {
                    this._fail(apiErrorMessage(error) ?? 'document.edit.aiEdit.errorSend');
                    return;
                }

                this.messages.update(messages => messages.filter(m => m.id !== userMessageId));
                this.currentRun.set(null);
                if (refused?.text && refused.text !== active.message) this.draft.set(refused.text);
                this._attach(active);
            });
    }

    /** O pedido em andamento de uma lista de histórico (o último, se houver). */
    private _activeEntry(entries: AiEditHistoryEntryDto[]): AiEditHistoryEntryDto | null {
        const last = entries.at(-1);
        return last && ACTIVE_STAGES.has(last.stage) ? last : null;
    }

    /**
     * Passa a acompanhar um pedido que já está rodando no servidor: a fala dele aparece (ou
     * é reaproveitada, com o aviso de erro que tinha ficado embaixo saindo da conversa), e o
     * "pensando" volta para a tela até o `done`.
     */
    private _attach(entry: AiEditHistoryEntryDto): void {
        this._abandoned.delete(entry.taskId);
        const attachments = (entry.attachments ?? []).map(a => ({
            url: a.url,
            type: a.type,
            thumbUrl: a.thumbUrl,
        }));

        let userMessageId = '';
        this.messages.update(messages => {
            const list = [...messages];
            const existing = [...list]
                .reverse()
                .find(m => m.role === 'user' && m.text.trim() === entry.message.trim());
            if (existing) {
                userMessageId = existing.id;
                const at = list.indexOf(existing);
                return list.filter(
                    (m, i) => !(i > at && m.error && m.retryText?.trim() === entry.message.trim())
                );
            }
            const bubble: AiEditMessage = {
                id: newId(),
                role: 'user',
                text: entry.message,
                createdAt: entry.createdAt,
                media: attachments.map(a => ({ url: a.url, kind: a.type, thumbUrl: a.thumbUrl })),
            };
            userMessageId = bubble.id;
            return [...list, bubble];
        });

        this.currentRun.set({ taskId: entry.taskId, userMessageId, text: entry.message, attachments });
        this._early = [];
        if (entry.stage === 'applying' && entry.changes?.length) {
            this.stage.set('applying');
            this.changes.set(entry.changes.map(change => ({ ...change })));
        } else {
            this.stage.set('thinking');
            this.changes.set([]);
        }
        this.stageText.set(this._translate.instant('document.edit.aiEdit.resuming'));
        this._startClock(Date.parse(entry.createdAt) || Date.now());
        this._armTimeout();
    }

    /**
     * Para o pedido em andamento. O servidor fecha a chamada à IA e não aplica o resto; o
     * que já tinha entrado fica (e pode ser desfeito). O fim chega pelo evento `cancelled`.
     */
    public cancel(): void {
        const run = this.currentRun();
        const documentId = this._editData.document()?._id;
        if (!run?.taskId || !documentId || this.cancelling()) return;

        this.cancelling.set(true);
        this.stageText.set(this._translate.instant('document.edit.aiEdit.cancelling'));
        this._controller
            .cancel(run.taskId, documentId)
            .pipe(take(1))
            .subscribe({
                // Se o evento se perder, a consulta de situação fecha o pedido.
                next: () => this._armTimeout(),
                error: () => this.cancelling.set(false),
            });
    }

    // ──────────────────────────────────────────────────────── anexos

    /**
     * Anexa fotos e vídeos (do botão, de arrastar ou de colar). Cada um sobe na hora, com
     * progresso; foto é comprimida antes, vídeo ganha miniatura e duração. O que não é
     * foto nem vídeo é ignorado, e o total para no teto de anexos por pedido.
     */
    public addFiles(files: FileList | File[]): void {
        const room = AI_EDIT_MAX_ATTACHMENTS - this.attachments().length;
        const accepted = Array.from(files ?? [])
            .filter(file => file.type.startsWith('image/') || file.type.startsWith('video/'))
            .slice(0, Math.max(0, room));

        for (const file of accepted) {
            const kind = file.type.startsWith('video/') ? 'video' : 'image';
            const attachment: AiEditPendingAttachment = {
                id: newId(),
                kind,
                name: file.name,
                previewUrl: URL.createObjectURL(file),
                status: 'uploading',
                progress: 0,
                size: file.size,
            };
            this.attachments.update(list => [...list, attachment]);
            this._upload(attachment.id, file, kind);
        }
    }

    /** Tira um anexo da fila (e apaga o arquivo, se ele já tinha subido). */
    public removeAttachment(id: string): void {
        const attachment = this.attachments().find(a => a.id === id);
        if (!attachment) return;

        URL.revokeObjectURL(attachment.previewUrl);
        this.attachments.update(list => list.filter(a => a.id !== id));
        const keys = [attachment.url, attachment.thumbUrl].filter((k): k is string => !!k);
        if (keys.length) this._media.deleteMedia(keys).pipe(take(1)).subscribe({ error: () => undefined });
    }

    /** "Tentar de novo" numa falha: reenvia o mesmo pedido. */
    public retry(message: AiEditMessage): void {
        if ((!message.retryText && !message.retryAttachments?.length) || this.busy()) return;
        this.send(message.retryText ?? '', message.retryAttachments);
    }

    /**
     * Decisão num conflito (estado 5): "usar a da IA" grava a versão da IA por cima da
     * atual; "manter a dela" fecha a pendência. Só com o pedido terminado (a API recusa no
     * meio dele). O cartão trava até a resposta; se alguém mexeu de novo nesse meio tempo,
     * a alteração volta como conflito, com a versão nova da pessoa.
     */
    public resolveConflict(
        message: AiEditMessage,
        change: AiEditChange,
        resolution: AiEditConflictResolution
    ): void {
        this._settle(message, change, documentId =>
            this._controller.resolveConflict(message.taskId!, change.id, documentId, resolution)
        );
    }

    /** "Tentar de novo" numa alteração que falhou (estado 6): reaplica só ela. */
    public retryChange(message: AiEditMessage, change: AiEditChange): void {
        this._settle(message, change, documentId =>
            this._controller.retryChange(message.taskId!, change.id, documentId)
        );
    }

    private _settle(
        message: AiEditMessage,
        change: AiEditChange,
        call: (documentId: string) => Observable<AiEditChange>
    ): void {
        const documentId = this._editData.document()?._id;
        if (!message.taskId || !documentId || this.busy() || this.settling()) return;

        this.settling.set(change.id);
        this._editData.markDiscretePreviewChange();
        call(documentId)
            .pipe(
                take(1),
                finalize(() => this.settling.set(null))
            )
            .subscribe({
                next: updated => this._changeSettled(message.taskId!, updated),
                // A rede falhando vira toast (controller); o cartão continua como estava.
                error: () => undefined,
            });
    }

    /**
     * A alteração como ficou depois da decisão: o cartão, o realce da prévia e o Desfazer
     * acompanham. Entrou no documento: flash azul no bloco e a prévia vai até ele.
     */
    private _changeSettled(taskId: string, updated: AiEditChange): void {
        this._patchChange(taskId, updated);

        if (updated.status !== 'applied') {
            this._showPending();
            return;
        }

        this._editData.markDiscretePreviewChange();
        this._editData.setPreviewHighlight({
            itemIds: updated.fieldIds,
            tone: 'done',
            marks: pendingMarks(this._attentionChanges()),
        });
        const landing = this._landingTarget([updated]);
        if (landing) this._editData.requestPreviewScroll(landing);
        clearTimeout(this._flashTimer);
        this._flashTimer = setTimeout(() => this._showPending(), AI_EDIT_DONE_FLASH_MS);
    }

    /**
     * Evento `ai-edit-change`: a alteração foi decidida (ou retentada) neste ou em outro
     * aparelho. Mesma situação que o cartão já mostra = eco do que acabou de ser feito aqui.
     */
    public onChangeUpdated(event: AiEditChangeEvent): void {
        const change = event?.change;
        if (!event?.taskId || !change?.id) return;
        const current = this.messages()
            .find(message => message.taskId === event.taskId)
            ?.changes?.find(c => c.id === change.id);
        if (!current || current.status === change.status) return;

        this._patchChange(event.taskId, change);
        this._showPending();
    }

    private _patchChange(taskId: string, updated: AiEditChange): void {
        this.messages.update(messages =>
            messages.map(message =>
                message.taskId === taskId && message.changes
                    ? {
                          ...message,
                          changes: message.changes.map(change =>
                              change.id === updated.id
                                  ? { ...change, ...updated, conflict: updated.conflict, error: updated.error }
                                  : change
                          ),
                      }
                    : message
            )
        );
    }

    /** As alterações do pedido que tem pendências (o que `attention` aponta). */
    private _attentionChanges(): AiEditChange[] {
        const taskId = this.attention()?.taskId;
        return taskId ? (this.messages().find(message => message.taskId === taskId)?.changes ?? []) : [];
    }

    /** A prévia mostra (ou deixa de mostrar) os blocos em conflito e os que falharam. */
    private _showPending(): void {
        const marks = pendingMarks(this._attentionChanges());
        const highlight: PreviewHighlight | null = marks.length ? { itemIds: [], tone: 'conflict', marks } : null;
        this._editData.setPreviewHighlight(highlight);
    }

    /** Desfaz em lote as alterações de um pedido (ilha, chip "Desfazer a última alteração"). */
    public undo(taskId: string): void {
        const documentId = this._editData.document()?._id;
        if (this.reverting() || this.busy() || !documentId) return;

        this.reverting.set(true);
        this._dismissUndo();
        this._editData.markDiscretePreviewChange();

        this._controller
            .revert(taskId, documentId)
            .pipe(
                take(1),
                finalize(() => this.reverting.set(false))
            )
            .subscribe({
                next: () => {
                    this.messages.update(messages =>
                        messages.map(message =>
                            message.taskId === taskId ? { ...message, reverted: true } : message
                        )
                    );
                    // Pedido desfeito não tem mais pendência: o realce da prévia sai.
                    this._showPending();
                },
            });
    }

    // ───────────────────────────────────────────────── eventos (Pusher)

    /** Evento `ai-edit-status`. Pedidos de outros usuários no mesmo documento são ignorados. */
    public onStatus(event: AiEditStatusEvent): void {
        if (!event?.taskId) return;
        if (!this.currentRun()) this._resumeAbandoned(event.taskId);
        const run = this.currentRun();
        if (!run) return;

        if (!run.taskId) {
            // Ainda sem o 202: guarda o que é deste usuário para aplicar quando ele chegar.
            if (!event.userId || event.userId === this._currentUserId()) this._early.push(event);
            return;
        }

        if (event.taskId !== run.taskId) return;
        this._armTimeout();

        switch (event.stage) {
            case 'queued':
            case 'planning':
                this.stage.set('thinking');
                this.stageText.set(event.message ?? null);
                return;

            case 'applying':
                this._applying(event);
                return;

            case 'done':
                this._done(run, event);
                return;

            case 'error':
                this._error(event);
                return;

            case 'cancelled':
                this._cancelled(run, event);
                return;
        }
    }

    /** Pedido parado pelo usuário: vira uma resposta com o que chegou a entrar. */
    private _cancelled(run: AiEditRun, event: AiEditStatusEvent): void {
        clearTimeout(this._timeout);
        const changes = (event.changes ?? []).filter(change => change.status === 'applied');

        this.messages.update(messages => [
            ...messages.map(message => (message.sending ? { ...message, sending: false } : message)),
            {
                id: newId(),
                role: 'assistant',
                text: event.reply?.trim() || this._translate.instant('document.edit.aiEdit.cancelledDefault'),
                createdAt: new Date().toISOString(),
                taskId: run.taskId ?? undefined,
                changes,
                suggestions: [],
                cancelled: true,
            },
        ]);

        this._endRun();
        if (changes.length) this._editData.markDiscretePreviewChange();
    }

    /** Fecha o estado do pedido em andamento (relógio, etapa, realce). */
    private _endRun(): void {
        this.changes.set([]);
        this.stage.set('idle');
        this.stageText.set(null);
        this.currentRun.set(null);
        this.cancelling.set(false);
        this._early = [];
        this._stopClock();
        this._editData.setPreviewHighlight(null);
    }

    private _startClock(startedAt: number): void {
        this._runStartedAt.set(startedAt);
        this._now.set(Date.now());
        clearInterval(this._clock);
        this._clock = setInterval(() => this._now.set(Date.now()), 1000);
    }

    private _stopClock(): void {
        clearInterval(this._clock);
        this._clock = undefined;
        this._runStartedAt.set(null);
    }

    /**
     * Evento `ai-edit-conflict`: uma pessoa editou o campo enquanto a IA trabalhava. A IA
     * NUNCA sobrescreve: a alteração fica em conflito, com quem editou e as duas versões, e
     * o cartão de decisão ("usar a da IA / manter a dela") aparece quando o pedido termina.
     */
    public onConflict(event: AiEditConflictEvent): void {
        if (!event?.taskId || event.taskId !== this.currentRun()?.taskId) return;
        const conflict: AiEditConflictInfo = {
            userId: event.userId,
            userName: event.userName ?? '',
            userAvatar: event.userAvatar,
            aiPreview: event.aiPreview ?? '',
            humanPreview: event.humanPreview ?? '',
            at: event.at,
        };
        this.changes.update(changes =>
            changes.map(change =>
                change.id === event.changeId ? { ...change, status: 'conflict', conflict } : change
            )
        );
    }

    /** Saída do editor: a conversa é deste documento e desta visita. */
    public clear(): void {
        clearTimeout(this._timeout);
        clearTimeout(this._flashTimer);
        clearTimeout(this._undoTimer);
        this._early = [];
        this._restoredFor = null;
        this.restoring.set(false);
        this._abandoned.clear();
        this._stopClock();
        this.cancelling.set(false);
        this.settling.set(null);
        this.messages.set([]);
        this.draft.set('');
        this._clearAttachments(true);
        this.stage.set('idle');
        this.stageText.set(null);
        this.changes.set([]);
        this.currentRun.set(null);
        this.undoOffer.set(null);
        this.reverting.set(false);
        this._editData.setPreviewHighlight(null);
    }

    // ───────────────────────────────────────── ilha de Desfazer (8 s)

    /** Mouse sobre a ilha: o tempo para (ninguém perde o Desfazer enquanto lê). */
    public pauseUndo(): void {
        if (!this.undoOffer() || !this._undoTimer) return;
        clearTimeout(this._undoTimer);
        this._undoTimer = undefined;
        this._undoRemaining = Math.max(0, this._undoDeadline - Date.now());
    }

    public resumeUndo(): void {
        if (!this.undoOffer() || this._undoTimer) return;
        this._startUndoTimer(this._undoRemaining || AI_EDIT_UNDO_WINDOW_MS);
    }

    // ─────────────────────────────────────────────────────── internos

    private _accepted(userMessageId: string, taskId: string): void {
        const run = this.currentRun();
        if (!run || run.userMessageId !== userMessageId) return;

        this.currentRun.set({ ...run, taskId });
        this.messages.update(messages =>
            messages.map(message =>
                message.id === userMessageId ? { ...message, sending: false } : message
            )
        );

        // Nunca mudo por mais de ~400 ms: com o 202 na mão, a IA já está "pensando".
        if (this.stage() === 'sending') this.stage.set('thinking');

        const early = this._early.filter(event => event.taskId === taskId);
        this._early = [];
        early.forEach(event => this.onStatus(event));
    }

    private _applying(event: AiEditStatusEvent): void {
        this.stage.set('applying');

        if (event.changes?.length) this.changes.set(event.changes.map(change => ({ ...change })));

        const index = event.changeIndex;
        if (index == null) return;

        // Tudo antes do índice terminou; ele está em andamento; o resto espera. Conflito e
        // erro não voltam a "aplicada" por aqui: quem os decide é o usuário, depois.
        this.changes.update(changes =>
            changes.map((change, i) => {
                if (ATTENTION_STATUSES.has(change.status)) return change;
                if (i < index) return { ...change, status: 'applied' };
                if (i === index) return { ...change, status: 'running' };
                return { ...change, status: 'pending' };
            })
        );

        const running = this.changes()[index];
        if (!running) return;

        // O bloco afetado pulsa na prévia, que rola até ele; e a mutação que vem a seguir
        // repagina em 150 ms em vez de esperar o silêncio da digitação.
        this._editData.markDiscretePreviewChange();
        this._editData.setPreviewHighlight({ itemIds: running.fieldIds, tone: 'running' });
        const first = running.fieldIds[0];
        if (first && this._editData.findFieldAnywhere(first)) {
            this._editData.requestPreviewScroll(first);
        }
    }

    private _done(run: AiEditRun, event: AiEditStatusEvent): void {
        clearTimeout(this._timeout);

        const source = event.changes?.length ? event.changes : this.changes();
        const changes = source.map(change =>
            ATTENTION_STATUSES.has(change.status) || change.status === 'kept'
                ? { ...change }
                : { ...change, status: 'applied' as const }
        );
        const applied = changes.filter(change => change.status === 'applied');

        this.messages.update(messages => [
            ...messages,
            {
                id: newId(),
                role: 'assistant',
                text: event.reply?.trim() || this._translate.instant('document.edit.aiEdit.doneDefault'),
                createdAt: new Date().toISOString(),
                taskId: run.taskId!,
                changes,
                suggestions: event.suggestions ?? [],
                webSearched: !!event.webSearched,
            },
        ]);

        this.changes.set([]);
        this.stage.set('idle');
        this.stageText.set(null);
        this.currentRun.set(null);
        this.cancelling.set(false);
        this._stopClock();

        // Flash azul que esmaece nos blocos alterados.
        this._editData.markDiscretePreviewChange();
        this._editData.setPreviewHighlight({
            itemIds: applied.flatMap(change => change.fieldIds),
            tone: 'done',
        });

        // E a prévia vai até lá: o usuário vê o resultado sem procurar (uma seção nova
        // nasce no fim do documento; a capa e o título ficam na primeira folha).
        const landing = this._landingTarget(applied);
        if (landing) this._editData.requestPreviewScroll(landing);
        // Passado o flash, ficam só as pendências (conflito em laranja, falha em vermelho).
        clearTimeout(this._flashTimer);
        this._flashTimer = setTimeout(() => this._showPending(), AI_EDIT_DONE_FLASH_MS);

        if (applied.length) {
            this.undoOffer.set({ taskId: run.taskId!, count: applied.length });
            this._startUndoTimer(AI_EDIT_UNDO_WINDOW_MS);
        }
    }

    /**
     * Onde a prévia deve parar quando o pedido termina: o primeiro item que a IA mexeu
     * ou, para alterações sem item (capa, título do documento), o topo.
     */
    private _landingTarget(applied: AiEditChange[]): string | null {
        for (const change of applied) {
            if (change.fieldIds[0]) return change.fieldIds[0];
            if (COVER_KINDS.has(change.kind)) return PREVIEW_TOP_ITEM;
        }
        return null;
    }

    private _error(event: AiEditStatusEvent): void {
        // Erro de UMA alteração (estado 6): ela fica marcada, com o motivo, e o pedido
        // segue. O "tentar de novo" dela aparece no cartão quando o pedido termina.
        if (event.changeId) {
            this.changes.update(changes =>
                changes.map(change =>
                    change.id === event.changeId ? { ...change, status: 'error', error: event.error } : change
                )
            );
            return;
        }

        this._fail(event.error ?? 'document.edit.aiEdit.errorDefault');
    }

    /** O pedido inteiro falhou: a conversa diz o quê, o que já foi aplicado continua. */
    private _fail(reason: string): string {
        clearTimeout(this._timeout);
        const run = this.currentRun();
        const id = newId();

        this.messages.update(messages => [
            ...messages.map(message => (message.sending ? { ...message, sending: false } : message)),
            {
                id,
                role: 'assistant',
                text: reason,
                createdAt: new Date().toISOString(),
                error: true,
                retryText: run?.text,
                retryAttachments: run?.attachments?.length ? run.attachments : undefined,
            },
        ]);

        this._endRun();
        return id;
    }

    /**
     * Cada evento do pedido rearma a vigia. Ela NÃO desiste por tempo: depois de um
     * silêncio, pergunta ao servidor como está o pedido. Vivo, segue esperando (o tempo que
     * for); terminado, mostra o resultado; morto (a API reiniciou no meio), avisa.
     */
    private _armTimeout(): void {
        clearTimeout(this._timeout);
        this._timeout = setTimeout(() => this._checkRun(), AI_EDIT_SILENCE_CHECK_MS);
    }

    private _checkRun(): void {
        const run = this.currentRun();
        const documentId = this._editData.document()?._id;
        if (!run) return;
        if (!run.taskId || !documentId) {
            this._armTimeout();
            return;
        }

        this._controller
            .runStatus(documentId, run.taskId)
            .pipe(take(1))
            .subscribe({
                next: status => this._applyRunStatus(run, status),
                // Sem resposta (rede): continua esperando e pergunta de novo depois.
                error: () => this._armTimeout(),
            });
    }

    private _applyRunStatus(run: AiEditRun, status: AiEditRunStatusDto): void {
        if (this.currentRun()?.taskId !== run.taskId) return;
        const event: AiEditStatusEvent = {
            taskId: status.taskId,
            stage: status.stage,
            changes: status.changes,
            reply: status.reply,
            suggestions: status.suggestions,
            webSearched: status.webSearched,
        };

        switch (status.stage) {
            case 'done':
                this._done(run, event);
                return;
            case 'cancelled':
                this._cancelled(run, event);
                return;
            case 'error': {
                // Interrompido: o servidor não tem mais o pedido rodando. Se ele voltar a
                // dar notícia, o acompanhamento é retomado.
                const errorMessageId = this._fail(status.error || 'document.edit.aiEdit.errorDefault');
                this._abandoned.set(run.taskId!, { ...run, errorMessageId });
                return;
            }
            default:
                if (status.stage === 'applying' && status.changes?.length && !this.changes().length)
                    this.changes.set(status.changes.map(change => ({ ...change })));
                this._armTimeout();
        }
    }

    /** Evento de um pedido dado como perdido: tira o aviso de erro e volta a acompanhar. */
    private _resumeAbandoned(taskId: string): void {
        const back = this._abandoned.get(taskId);
        if (!back || this.busy()) return;
        this._abandoned.delete(taskId);
        const { errorMessageId, ...run } = back;
        this.messages.update(messages => messages.filter(m => m.id !== errorMessageId));
        this.currentRun.set(run);
        this.stage.set('thinking');
        this.stageText.set(null);
    }

    private _startUndoTimer(ms: number): void {
        clearTimeout(this._undoTimer);
        this._undoDeadline = Date.now() + ms;
        this._undoRemaining = ms;
        this._undoTimer = setTimeout(() => this._dismissUndo(), ms);
    }

    private _dismissUndo(): void {
        clearTimeout(this._undoTimer);
        this._undoTimer = undefined;
        this.undoOffer.set(null);
    }

    /** Anexos já no storage, no formato do pedido. */
    private _readyAttachments(): AiEditAttachmentDto[] {
        return this.attachments()
            .filter(a => a.status === 'done' && !!a.url)
            .map(a => ({
                url: a.url!,
                type: a.kind,
                size: a.size,
                thumbUrl: a.thumbUrl,
                duration: a.duration,
            }));
    }

    /**
     * Esvazia a fila de anexos. `deleteUploaded` apaga do storage o que subiu e não foi
     * enviado (saída do editor); ao enviar, os arquivos seguem no pedido.
     */
    private _clearAttachments(deleteUploaded: boolean): void {
        const list = this.attachments();
        list.forEach(a => URL.revokeObjectURL(a.previewUrl));
        this.attachments.set([]);

        if (!deleteUploaded) return;
        const keys = list.flatMap(a => [a.url, a.thumbUrl]).filter((k): k is string => !!k);
        if (keys.length) this._media.deleteMedia(keys).pipe(take(1)).subscribe({ error: () => undefined });
    }

    private _patchAttachment(id: string, patch: Partial<AiEditPendingAttachment>): void {
        this.attachments.update(list => list.map(a => (a.id === id ? { ...a, ...patch } : a)));
    }

    private _upload(id: string, file: File, kind: 'image' | 'video'): void {
        const mediaType = kind === 'video' ? DocumentItemTypeEnum.VIDEO : DocumentItemTypeEnum.IMAGE;
        // Foto comprimida antes de subir (como no editor); vídeo sobe como veio.
        const prepared$: Observable<File> =
            kind === 'image' ? this._mediaUtils.compressImage(file).pipe(take(1)) : of(file);

        prepared$
            .pipe(
                switchMap(ready => {
                    this._patchAttachment(id, { size: ready.size });
                    return this._media.uploadMedia(ready, mediaType, progress =>
                        this._patchAttachment(id, { progress })
                    );
                }),
                take(1)
            )
            .subscribe({
                next: ({ url }) => {
                    // Saiu da fila enquanto subia: o arquivo não tem dono, apaga.
                    if (!this.attachments().some(a => a.id === id)) {
                        this._media.deleteMedia([url]).pipe(take(1)).subscribe({ error: () => undefined });
                        return;
                    }
                    this._patchAttachment(id, { url, status: kind === 'video' ? 'uploading' : 'done', progress: 100 });
                    if (kind === 'video') this._attachVideoPoster(id, file);
                },
                error: () => this._patchAttachment(id, { status: 'error' }),
            });
    }

    /** Miniatura e duração do vídeo: a IA vê a miniatura e o documento a usa no card. */
    private _attachVideoPoster(id: string, file: File): void {
        from(this._mediaUtils.getVideoPosterAndDuration(file))
            .pipe(
                switchMap(({ poster, durationSeconds }) => {
                    if (durationSeconds) this._patchAttachment(id, { duration: durationSeconds });
                    return poster ? this._media.uploadMedia(poster, DocumentItemTypeEnum.IMAGE) : EMPTY;
                }),
                take(1),
                finalize(() => this._patchAttachment(id, { status: 'done' }))
            )
            .subscribe({
                next: ({ url }) => this._patchAttachment(id, { thumbUrl: url }),
                error: () => undefined,
            });
    }

    /** A conversa como a IA precisa ler: só o texto das falas, sem as falhas. */
    private _turns(): AiEditTurn[] {
        return this.messages()
            .filter(message => !message.error && (message.text.trim() || message.media?.length))
            .map(message => ({
                role: message.role,
                // Marcador para a IA saber que a fala trouxe mídia (não é exibido a ninguém).
                text: message.text.trim() || `[${message.media?.length ?? 0} anexo(s)]`,
            }));
    }

    private _currentUserId(): string | null {
        try {
            return this._userController.getCurrentUserId();
        } catch {
            return null;
        }
    }
}
